import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'riskninja-install-dismissed-at';
const DISMISS_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

const wasRecentlyDismissed = (): boolean => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - Number(v) < DISMISS_TTL;
  } catch { return false; }
};

const isStandalone = (): boolean => {
  // iOS: navigator.standalone; everything else: display-mode
  if (typeof window === 'undefined') return false;
  // @ts-expect-error iOS-only
  if (window.navigator.standalone) return true;
  return window.matchMedia('(display-mode: standalone)').matches;
};

const InstallPrompt: React.FC = () => {
  const location = useLocation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!location.pathname.startsWith('/m')) return;
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [location.pathname]);

  if (!show || !deferred) return null;

  const install = async () => {
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } finally {
      setShow(false);
      setDeferred(null);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  return (
    <div className="fixed inset-x-3 bottom-24 z-40 flex items-center gap-3 rounded-xl bg-white p-3 shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
      <img src="/logo192.png" alt="" className="h-9 w-9 rounded-lg" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 dark:text-white">Install Risk Ninja</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">Add to home screen for one-tap access and camera capture.</div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400"
      >
        Later
      </button>
      <button
        type="button"
        onClick={install}
        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
        style={{ backgroundColor: '#1993e5' }}
      >
        Install
      </button>
    </div>
  );
};

export default InstallPrompt;
